/**
 * Centralized Logging Utility
 *
 * Single source of truth for all logging in the application.
 * Provides consistent formatting and log levels.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogConfig {
  enableDebug: boolean;
  enableInfo: boolean;
  enableWarn: boolean;
  enableError: boolean;
  prefix?: string;
}

const importMetaEnv = (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env;

const defaultConfig: LogConfig = {
  enableDebug: Boolean(importMetaEnv?.DEV),
  enableInfo: true,
  enableWarn: true,
  enableError: true,
};

class Logger {
  private config: LogConfig = defaultConfig;

  configure(config: Partial<LogConfig>) {
    this.config = { ...this.config, ...config };
  }

  private formatMessage(level: LogLevel, context: string, message: string): string {
    const timestamp = new Date().toISOString();
    const prefix = this.config.prefix ? `[${this.config.prefix}]` : '';
    return `${prefix}[${timestamp}][${level.toUpperCase()}][${context}] ${message}`;
  }

  debug(context: string, message: string, ...args: unknown[]) {
    if (this.config.enableDebug) {
      console.log(this.formatMessage('debug', context, message), ...args);
    }
  }

  info(context: string, message: string, ...args: unknown[]) {
    if (this.config.enableInfo) {
      console.log(this.formatMessage('info', context, message), ...args);
    }
  }

  warn(context: string, message: string, ...args: unknown[]) {
    if (this.config.enableWarn) {
      console.warn(this.formatMessage('warn', context, message), ...args);
    }
  }

  error(context: string, message: string, error?: unknown, ...args: unknown[]) {
    if (this.config.enableError) {
      console.error(this.formatMessage('error', context, message), error, ...args);
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience functions
export const log = {
  debug: (context: string, message: string, ...args: unknown[]) => logger.debug(context, message, ...args),
  info: (context: string, message: string, ...args: unknown[]) => logger.info(context, message, ...args),
  warn: (context: string, message: string, ...args: unknown[]) => logger.warn(context, message, ...args),
  error: (context: string, message: string, error?: unknown, ...args: unknown[]) => logger.error(context, message, error, ...args),
};
